import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface Member {
  id: string;
  userId: string;
  role: string;
  createdAt: string;
  email: string;
  name: string | null;
}

export interface Invite {
  id: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "revoked";
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  invitedByEmail: string | null;
  invitedByName: string | null;
  acceptedByEmail: string | null;
  isExpired: boolean;
}

const memberKeys = {
  list: (solutionId: string) => ["solution-members", solutionId] as const,
};

const inviteKeys = {
  list: (solutionId: string) => ["solution-invites", solutionId] as const,
};

const pendingInviteKeys = {
  mine: () => ["pending-invites", "mine"] as const,
};

export interface PendingInvite {
  id: string;
  solutionId: string;
  expiresAt: string;
  solutionName: string | null;
  clientName: string | null;
  invitedByName: string | null;
  invitedByEmail: string | null;
}

export function usePendingInvites() {
  return useQuery({
    queryKey: pendingInviteKeys.mine(),
    queryFn: async (): Promise<PendingInvite[]> => {
      const res = await fetch(`/api/me/pending-invites`);
      if (!res.ok) throw new Error("Failed to fetch pending invites");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useClaimInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      inviteId: string,
    ): Promise<{ solutionId: string; solutionName: string; alreadyMember: boolean }> => {
      const res = await fetch(`/api/me/pending-invites/${inviteId}/claim`, { method: "POST" });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.error?.message ?? "Failed to claim invite");
      }
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pendingInviteKeys.mine() });
      qc.invalidateQueries({ queryKey: ["solutions"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useMembers(solutionId: string) {
  return useQuery({
    queryKey: memberKeys.list(solutionId),
    queryFn: async (): Promise<Member[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/members`);
      if (!res.ok) throw new Error("Failed to fetch members");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useInvites(solutionId: string) {
  return useQuery({
    queryKey: inviteKeys.list(solutionId),
    queryFn: async (): Promise<Invite[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/invites`);
      if (!res.ok) throw new Error("Failed to fetch invites");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export interface CreateInviteResult {
  id: string;
  token: string;
  email: string;
  role: string;
  expiresAt: string;
}

export function useCreateInvite(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string }): Promise<CreateInviteResult> => {
      const res = await fetch(`/api/solutions/${solutionId}/invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: input.email, role: "member" }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.error?.message ?? "Failed to create invite");
      }
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inviteKeys.list(solutionId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRevokeInvite(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await fetch(`/api/solutions/${solutionId}/invites/${inviteId}/revoke`, {
        method: "POST",
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.error?.message ?? "Failed to revoke invite");
      }
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inviteKeys.list(solutionId) });
      toast.success("Invite revoked");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
